import { ClubName } from 'app/components/club-name';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { localeAwareFormatDistance } from 'app/lib/date';
import { getImageVariantUrl } from 'app/lib/image-variants';
import type { FoundUser } from 'app/network/user';
import React from 'react';
import { useTranslation } from 'react-i18next';

export default function MemberCard(
  user: FoundUser & { showGroups?: boolean; subtext?: string; hasStreakContribution?: boolean },
) {
  const { t } = useTranslation();
  const { convertDistance } = useDistanceUnit();
  const groups =
    user.memberships?.map((m) => ({
      id: (m.groupId ?? m.group.id)!,
      name: m.group.name,
    })) ?? [];

  return (
    <Row
      alignItems="center"
      backgroundColor={user.hasStreakContribution ? 'surface.brand.secondary-3.subtle' : 'surface.primary-neutral'}
      borderRadius="radius.lg"
      gap="spacing.general.x4"
      padding="spacing.general.x4"
    >
      {user.profile?.picture ? (
        <PCImage
          aspectRatio={1}
          cachePolicy="memory-disk"
          height={80}
          recyclingKey={`${user.profile.picture}-member-card`}
          source={{ uri: getImageVariantUrl(user.profile.picture, 'medium') }}
          style={{ borderRadius: 40 }}
        />
      ) : null}

      <Column
        flex={1}
        gap={user.addresses?.[0] || user.showGroups ? 'spacing.general.x0,5' : 'spacing.general.x1'}
        justifyContent="space-around"
      >
        <Text fontWeight="700" numberOfLines={1} variant="bodyBase">
          {[user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ')}
        </Text>

        {user.addresses?.[0] ? (
          <Row gap="spacing.general.x0,5" margin="spacing.general.0" padding="spacing.general.0">
            <Text flexShrink={3} numberOfLines={1} variant="bodyBase">
              {user.addresses[0].city}
            </Text>
            {user.distance ? (
              <Text numberOfLines={1} variant="bodyBase">
                -
              </Text>
            ) : null}
            {user.distance ? (
              <Text fontWeight="700" numberOfLines={1} variant="bodyBase">
                {convertDistance(user.distance, { alreadyInKm: true, withUnit: true })}
              </Text>
            ) : null}
          </Row>
        ) : null}

        {user.subtext ? <Text variant="bodyBase">{user.subtext}</Text> : null}

        <MemberActivity user={user} t={t} />

        {user.showGroups ? (
          <Column style={{ marginTop: -5 }}>
            <ClubName groups={groups} preventNavigateToClubMembers />
          </Column>
        ) : null}
      </Column>

      {user.hasStreakContribution ? (
        <Column
          alignItems="center"
          backgroundColor="surface.brand.secondary-3.default"
          borderRadius="radius.full"
          height={40}
          justifyContent="center"
          width={40}
        >
          <Text fontSize={20}>🔥</Text>
        </Column>
      ) : null}
    </Row>
  );
}

function MemberActivity({
  user,
  t,
}: {
  user: FoundUser & { showGroups?: boolean; subtext?: string; hasStreakContribution?: boolean };
  t: ReturnType<typeof useTranslation>['t'];
}): React.ReactNode {
  return user.trustPoints || user.lastSeenAt ? (
    <Text color="text.placeholder" numberOfLines={1} style={{ width: '100%' }} variant="bodyBase">
      {user.trustPoints ? `👍 (${user.trustPoints.toFixed(0)})` : ''}
      {user.trustPoints && user.lastSeenAt ? ' | ' : ''}
      {!user.lastSeenAt
        ? ''
        : t('lastActiveAgo', {
            ago: localeAwareFormatDistance(new Date(), new Date(user.lastSeenAt)),
            ns: 'common',
          })}
    </Text>
  ) : null;
}
